/**
 * Cursive names — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const cursiveNamesPack: ToilePack = {
    slug: 'cursive-names',
    category: 'illustration',
    name: 'Cursive names',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'school', label: 'School cursive', subject: 'the name "Olivia" in clear school cursive on a ruled handwriting line, every join visible'},
        {key: 'flowing', label: 'Flowing script', subject: 'the name "Isabella" in a flowing script with a looped capital I and a long tail stroke under the word'},
        {key: 'necklace', label: 'Necklace name', subject: 'the name "Priya" in a continuous monoline cursive with no breaks, the shape of a name necklace'},
    ],
    styles: [
        {key: 'upright', label: 'Upright', style: 'upright cursive with a round, even stroke and every join plainly visible', palette: 'black ink only', framing: 'centered on a white landscape sheet with a generous margin'},
        {key: 'slanted', label: 'Slanted script', style: 'slanted flowing script with a light stroke and a graceful tail on the last letter', palette: 'black ink only', framing: 'centered on a white landscape sheet with a generous margin'},
        {key: 'monoline', label: 'Monoline', style: 'one continuous even monoline stroke, joined and unbroken from the first letter to the last', palette: 'black ink only', framing: 'centered on a white landscape sheet with a generous margin'},
    ],
    directions: ['longer name', 'two names', 'with a heart'],
    directionHint: 'try: longer name',
    seo: {
        title: 'Cursive Names: School, Flowing and Necklace Script',
        metaDescription: 'Nine cursive name sheets: Olivia, Isabella and Priya in school cursive, flowing script and one continuous monoline.',
        h1: 'Cursive names: a first name in school cursive, flowing script, monoline',
        tagline: 'Three first names, each written in three cursive hands.',
        intro: 'Cursive names are a first name written in cursive, for a necklace, a tattoo, a cake topper or a child learning to write it. The nine sheets set three names: Olivia in school cursive on a ruled line with every join visible, Isabella in flowing script with a looped capital I, and Priya in one continuous monoline stroke. Each name comes upright, slanted or as a single monoline. Ask for a longer name, two names or a heart.',
        useCases: [
            'a necklace or pendant design',
            'a cake topper',
            "a child's practice sheet for their name",
            'a tattoo reference',
        ],
        faq: [
            {q: 'Are the names spelled exactly?', a: 'Yes. Olivia, Isabella and Priya are spelled as written. A different name is a request.'},
            {q: 'What is the difference between upright and slanted?', a: 'Upright has a round, even stroke with every join visible. Slanted has a light stroke and a graceful tail on the last letter.'},
            {q: 'What is monoline?', a: 'One continuous, even stroke, joined from the first letter to the last. It suits a name on a necklace.'},
            {q: 'Can a child practice their name from these?', a: 'Yes. The school cursive sheet is set on a ruled line, with each join visible.'},
        ],
    },
}
